/**
 * Orders Page - Order History
 * 
 * Real-time updates: This page automatically receives order status updates
 * via WebSocket (see useOrderUpdates hook).
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrdersStore } from '@/store/orders';
import { useOrderUpdates } from '@/hooks/useOrderUpdates';
import { formatCurrency, formatTime, formatOrderId } from '@/lib/format';
import { Package, CheckCircle, XCircle, Clock } from 'lucide-react';

export default function OrdersPage() {
  const navigate = useNavigate();
  const { orders, activeOrders, pastOrders, isLoading, fetchOrders } = useOrdersStore();
  
  // Real-time order updates with connection status
  const { connectionState } = useOrderUpdates();

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'delivered':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'cancelled':
        return <XCircle className="w-5 h-5 text-red-600" />;
      default:
        return <Clock className="w-5 h-5 text-yellow-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'delivered':
        return 'bg-green-100 text-green-700';
      case 'cancelled':
        return 'bg-red-100 text-red-700';
      case 'out_for_delivery':
      case 'picked_up':
      case 'rider_enroute':
        return 'bg-blue-100 text-blue-700';
      case 'ready_for_pickup':
      case 'preparing':
        return 'bg-purple-100 text-purple-700';
      default:
        return 'bg-yellow-100 text-yellow-700';
    }
  };

  const getStatusText = (status: string) => {
    const statusMap: Record<string, string> = {
      pending: 'Order Placed',
      placed: 'Placed',
      accepted: 'Accepted',
      preparing: 'Preparing',
      ready_for_pickup: 'Ready',
      rider_enroute: 'Rider on the Way',
      picked_up: 'Picked Up',
      out_for_delivery: 'Out for Delivery',
      delivered: 'Delivered',
      cancelled: 'Cancelled',
      refunded: 'Refunded',
    };
    return statusMap[status] || status;
  };

  const getConnectionStatusColor = () => {
    switch (connectionState) {
      case 'connected':
        return 'bg-green-500';
      case 'connecting':
        return 'bg-yellow-500 animate-pulse';
      case 'disconnected':
      case 'error':
        return 'bg-red-500';
      default:
        return 'bg-gray-400';
    }
  };

  const getConnectionStatusText = () => {
    switch (connectionState) {
      case 'connected':
        return 'Live updates active';
      case 'connecting':
        return 'Connecting...';
      case 'disconnected':
        return 'Offline';
      case 'error':
        return 'Connection error';
      default:
        return '';
    }
  };

  return (
    <div className="bg-gray-50">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">My Orders</h1>
          {connectionState !== 'idle' && (
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <div className={`w-2 h-2 rounded-full ${getConnectionStatusColor()}`} />
              <span>{getConnectionStatusText()}</span>
            </div>
          )}
        </div>
      </div>

      <div>
        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading orders...</p>
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-24">
            <Package className="w-24 h-24 text-gray-300 mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-gray-700 mb-2">No orders yet</h2>
            <p className="text-gray-500 mb-8">Start browsing vendors to place your first order</p>
            <button
              onClick={() => navigate('/')}
              className="bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-8 rounded-lg"
            >
              Browse Vendors
            </button>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Active Orders */}
            {activeOrders.length > 0 && (
              <div>
                <h2 className="text-lg font-bold mb-4">Active Orders</h2>
                <div className="space-y-4">
                  {activeOrders.map((order) => (
                    <button
                      key={order.id}
                      onClick={() => navigate(`/order/${order.id}`)}
                      className="w-full bg-white border border-gray-200 rounded-lg p-4 hover:shadow-lg transition text-left"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-bold text-lg">{formatOrderId(order.id)}</h3>
                          <p className="text-sm text-gray-600">{order.vendor_name}</p>
                          <p className="text-xs text-gray-500 mt-1">{formatTime(order.created_at)}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {getStatusIcon(order.status)}
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(
                              order.status
                            )}`}
                          >
                            {getStatusText(order.status)}
                          </span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                      <span className="text-sm text-gray-600">{order.items?.length || 0} items</span>
                      <span className="font-bold text-green-600">{formatCurrency(order.total)}</span>
                    </div>
                  </button>
                  ))}
                </div>
              </div>
            )}

            {/* Past Orders */}
            {pastOrders.length > 0 && (
              <div>
                <h2 className="text-lg font-bold mb-4">Past Orders</h2>
                <div className="space-y-4">
                  {pastOrders.map((order) => (
                    <button
                      key={order.id}
                      onClick={() => navigate(`/order/${order.id}`)}
                      className="w-full bg-white border border-gray-200 rounded-lg p-4 hover:shadow-lg transition text-left opacity-75 hover:opacity-100"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-bold text-lg">{formatOrderId(order.id)}</h3>
                          <p className="text-sm text-gray-600">{order.vendor_name}</p>
                          <p className="text-xs text-gray-500 mt-1">{formatTime(order.created_at)}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {getStatusIcon(order.status)}
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(
                              order.status
                            )}`}
                          >
                            {getStatusText(order.status)}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                        <span className="text-sm text-gray-600">{order.items?.length || 0} items</span>
                        <span className="font-bold text-gray-600">{formatCurrency(order.total)}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
